import { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Filter as FilterIcon, Flag } from 'lucide-react'
import { dayOffsetFromToday } from '../lib/format'

/**
 * Shared task-filter pill — the same filter UX that lives on the
 * Spaces page (List / Board / Gantt views). Three sections:
 *
 *   • Assignee  — multi-select, with "Unassigned" + every team member
 *   • Priority  — multi-select, Urgent / High / Normal / Low / No priority
 *   • Due date  — single-select radio (Any / Overdue / Today / This week / This month / No due)
 *
 * Originally inlined inside `pages/employee/SpacePage.jsx`; lifted
 * into a shared module so the same filter UI ships on Team Tasks
 * and My Tasks without three copies of the same JSX falling out of
 * sync over time. Each page owns its own filter state and the
 * `filteredTasks` derivation — this component is purely the popover.
 *
 * Props:
 *   members            — `[{ userId, fullName, avatarUrl? }]` —
 *                        people the picker can target. For My Tasks
 *                        you can pass an empty array to hide the
 *                        section.
 *   filterAssignees    — `Set<userId | '__unassigned__'>` of
 *                        currently-selected assignees.
 *   setFilterAssignees — (Set) => void
 *   filterPriorities   — `Set<'urgent'|'high'|'normal'|'low'|'none'>`
 *   setFilterPriorities — (Set) => void
 *   filterDueRange     — one of DUE_RANGE_OPTIONS values
 *   setFilterDueRange  — (value) => void
 *   activeCount        — total active filters across all three
 *                        sections; drives the small accent badge
 *                        on the pill button.
 *   renderAvatar       — (member|null) => ReactNode — page-specific
 *                        avatar render (Avatar component from
 *                        components/Avatar.jsx, or the local
 *                        AvatarStack from SpacePage). When omitted,
 *                        the row shows just initials.
 */

export const DUE_RANGE_OPTIONS = [
  { value: 'all', label: 'Any time' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'no_due', label: 'No due date' },
]

export const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)' },
  high: { label: 'High', color: 'var(--color-priority-high)' },
  normal: { label: 'Medium', color: 'var(--color-priority-normal)' },
  low: { label: 'Low', color: 'var(--color-priority-low)' },
}

const PRIORITY_KEYS = ['urgent', 'high', 'normal', 'low']

/** Day window helpers used by every consumer's `filteredTasks` memo. */
function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
function endOfDay(d) {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

/**
 * Same matching logic the Spaces page uses for its `filterDueRange`
 * dropdown. Exported so consumers can `task.passes(dueRange,
 * dueDate)` without duplicating the date math.
 *
 * `completedAt` (optional) is the task's completion timestamp. It only
 * affects the `overdue` range: a completed task is NEVER overdue, even
 * if its due date is in the past — "overdue" means "past due AND still
 * open". This matches the canonical server-side definition used by the
 * dashboard "Overdue Across Team" card (me.service.workspaceOverview:
 * `completedAt: null` AND `dueDate < today`). Without this, completed
 * past-due tasks leaked into the Team Tasks / My Tasks overdue filter,
 * inflating the list and showing a "Complete" group — so the page count
 * never matched the card. Callers that don't pass `completedAt` fall
 * back to the old date-only behaviour for non-overdue ranges (and treat
 * the task as open for `overdue`).
 */
export function dueRangeMatches(
  range,
  dueDateIso,
  completedAt = null,
  dueDateHasTime = false,
) {
  if (range === 'all') return true
  if (range === 'no_due') return !dueDateIso
  if (!dueDateIso) return false
  const due = new Date(dueDateIso)
  const today = startOfDay(new Date())
  // `today` bucket + the week / month ranges stay CALENDAR-based in the
  // tenant display timezone (not the browser zone), so a date-only due date
  // isn't shifted across the UTC boundary.
  const offset = dayOffsetFromToday(dueDateIso)
  // Overdue is TIME-AWARE: a task WITH an explicit due time is overdue the
  // instant that time passes; a date-only due date is overdue only once the
  // whole calendar day has passed. Mirrors the backend rule in
  // flowdesk-api/src/utils/task-overdue.js and the Members buildStatsByUser
  // logic, so a same-day task due at 12:30 PM reads as overdue at 12:31 —
  // not just after midnight. A completed task is never overdue.
  if (range === 'overdue') {
    if (completedAt) return false
    if (dueDateHasTime) return due.getTime() < Date.now()
    return offset != null && offset < 0
  }
  if (range === 'today') return offset === 0
  if (range === 'this_week') {
    const start = startOfDay(new Date())
    // ISO week: Monday → Sunday. Walk start back to Monday.
    const wd = start.getDay() // 0=Sun, 1=Mon, ...
    const back = wd === 0 ? 6 : wd - 1
    start.setDate(start.getDate() - back)
    const end = new Date(start)
    end.setDate(end.getDate() + 6)
    return due >= start && due <= endOfDay(end)
  }
  if (range === 'this_month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
    const end = endOfDay(
      new Date(today.getFullYear(), today.getMonth() + 1, 0),
    )
    return due >= start && due <= end
  }
  return true
}

export function TaskFilterPill({
  members,
  filterAssignees,
  setFilterAssignees,
  filterPriorities,
  setFilterPriorities,
  filterDueRange,
  setFilterDueRange,
  activeCount,
  renderAvatar,
  onClearAll,
}) {
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)

  // Position the floating popover under the trigger — and KEEP it glued there
  // while the user scrolls or resizes. The menu is `position: fixed`, so if
  // its coords were computed only once on open, the trigger would scroll away
  // and leave the menu floating over unrelated content. Re-running on every
  // scroll / resize makes it track the field.
  useEffect(() => {
    if (!open) return
    const updatePosition = () => {
      const el = triggerRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      // Anchor the popover's right edge to the trigger's right edge so
      // it doesn't overflow off the right side of the page. 288px is
      // the width of the popover (`w-72`).
      const POPOVER_W = 288
      // top/left are VIEWPORT-relative and getBoundingClientRect() is already
      // viewport-relative — adding window.scrollY pushed the menu off the
      // bottom of the viewport by the scroll offset, so on a scrolled page the
      // options were unreachable. Bug 13302 (DF_012). Also flip the menu ABOVE
      // the trigger when there isn't room below, so every option stays
      // on-screen.
      const MENU_MAX_H = 448 // matches the max-h-[28rem] cap on the menu
      const GAP = 4
      const spaceBelow = window.innerHeight - r.bottom
      const openUp = spaceBelow < MENU_MAX_H && r.top > spaceBelow
      setCoords({
        top: openUp ? undefined : r.bottom + GAP,
        bottom: openUp ? window.innerHeight - r.top + GAP : undefined,
        left: Math.max(8, r.right - POPOVER_W),
      })
    }
    updatePosition()
    // `capture: true` so a scroll on ANY ancestor scroll container (not just
    // the window) repositions the menu — the Team Tasks list scrolls inside
    // its own container, whose scroll events don't bubble to window.
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open])

  // Click-outside + Escape to close.
  useEffect(() => {
    if (!open) return
    function onMouseDown(e) {
      const t = e.target
      if (
        !menuRef.current?.contains(t) &&
        !triggerRef.current?.contains(t)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function toggleAssignee(id) {
    const next = new Set(filterAssignees)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setFilterAssignees(next)
  }
  function togglePriority(p) {
    const next = new Set(filterPriorities)
    if (next.has(p)) next.delete(p)
    else next.add(p)
    setFilterPriorities(next)
  }
  function clearAll() {
    // When the host page owns extra filter dimensions and/or URL state
    // (e.g. Team Tasks' completion filters + deep-link aliases), let it
    // clear everything; the local resets below are the fallback for
    // any consumer that doesn't pass onClearAll.
    if (onClearAll) {
      onClearAll()
      return
    }
    setFilterAssignees(new Set())
    setFilterPriorities(new Set())
    setFilterDueRange('all')
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border cursor-pointer ' +
          (activeCount > 0
            ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]'
            : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        {/* Label and icon size match the `ToolButton` filter trigger in
         *  components/table/DataTable.jsx, which every table page in the
         *  product uses — the two read as different controls otherwise
         *  (DF_031 / Mantis 22241). The pill's own geometry stays as it is so
         *  it still lines up with its Group / Customise siblings. */}
        <FilterIcon size={15} />
        Filters
        {activeCount > 0 ? (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] bg-[color:var(--color-accent)] text-white">
            {activeCount}
          </span>
        ) : null}
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: 'fixed',
                top: coords.top,
                bottom: coords.bottom,
                left: coords.left,
                zIndex: 60,
                maxHeight: 'calc(100vh - 16px)',
              }}
              className="w-72 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] max-h-[28rem] overflow-auto"
            >
              <div className="px-3 py-2 flex items-center justify-between border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                  Filters
                </span>
                {activeCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs text-[color:var(--color-priority-urgent)] hover:underline cursor-pointer"
                  >
                    Clear All
                  </button>
                ) : null}
              </div>

              {members && members.length > 0 ? (
                <div className="p-3 border-b border-[color:var(--color-rule)]">
                  <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2 mt-0">
                    Assignee
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterAssignees.has('__unassigned__')}
                        onChange={() => toggleAssignee('__unassigned__')}
                      />
                      {renderAvatar ? renderAvatar(null) : null}
                      <span className="text-[color:var(--color-ink-muted)]">
                        Unassigned
                      </span>
                    </label>
                    {members.map((m) => (
                      <label
                        key={m.userId}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterAssignees.has(m.userId)}
                          onChange={() => toggleAssignee(m.userId)}
                        />
                        {renderAvatar ? renderAvatar(m) : null}
                        <span className="truncate">{m.fullName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="p-3 border-b border-[color:var(--color-rule)]">
                <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                  Priority
                </p>
                <div className="space-y-1">
                  {PRIORITY_KEYS.map((p) => (
                    <label
                      key={p}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filterPriorities.has(p)}
                        onChange={() => togglePriority(p)}
                      />
                      <Flag
                        size={12}
                        fill={PRIORITY_META[p].color}
                        strokeWidth={0}
                      />
                      <span style={{ color: PRIORITY_META[p].color }}>
                        {PRIORITY_META[p].label}
                      </span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterPriorities.has('none')}
                      onChange={() => togglePriority('none')}
                    />
                    <Flag
                      size={12}
                      className="text-[color:var(--color-ink-subtle)]"
                    />
                    <span className="text-[color:var(--color-ink-muted)]">
                      No priority
                    </span>
                  </label>
                </div>
              </div>

              <div className="p-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                  Due date
                </p>
                <div className="space-y-1">
                  {DUE_RANGE_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="due-range"
                        checked={filterDueRange === opt.value}
                        onChange={() => setFilterDueRange(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
